interface GraphNode {
  id: string;
}

interface GraphLink {
  from: string;
  to: string;
}

interface Graph<TNode extends GraphNode> {
  nodes: TNode[];
  links: GraphLink[];
}

interface GraphTree<TNode extends GraphNode> {
  node: TNode;
  children: GraphTree<TNode>[];
}

const appendTo = (map: Map<string, string[]>, key: string, value: string) => {
  const values = map.get(key);
  if (values) values.push(value);
  else map.set(key, [value]);
};

const indexGraph = <TNode extends GraphNode>({ nodes, links }: Graph<TNode>) => {
  const byId = new Map(nodes.map(node => [node.id, node]));
  const parentIds = new Map<string, string[]>();
  const childIds = new Map<string, string[]>();
  for (const { from, to } of links) {
    if (!byId.has(from) || !byId.has(to)) continue;
    appendTo(parentIds, to, from);
    appendTo(childIds, from, to);
  }

  const resolve = (ids: string[] = []): TNode[] => ids.flatMap(id => byId.get(id) ?? []);
  const parents = (id: string): TNode[] => resolve(parentIds.get(id));
  const children = (id: string): TNode[] => resolve(childIds.get(id));
  const roots = (): TNode[] => nodes.filter(({ id }) => !parentIds.has(id));

  const levels = (id: string, next: (id: string) => TNode[]): TNode[][] => {
    const visited = new Set([id]);
    const result: TNode[][] = [];
    let level = [id];
    while (level.length > 0) {
      const nodesAtLevel = level.flatMap(next).filter(node => !visited.has(node.id) && visited.add(node.id));
      if (nodesAtLevel.length > 0) result.push(nodesAtLevel);
      level = nodesAtLevel.map(node => node.id);
    }
    return result;
  };

  const tree = (keep: (node: TNode) => boolean): GraphTree<TNode>[] => {
    const placed = new Set<string>();
    const placedChildren = new Map<string, TNode[]>();
    let level = roots();
    for (const { id } of level) placed.add(id);
    while (level.length > 0) {
      const nextLevel: TNode[] = [];
      for (const { id } of level) {
        const unplaced = children(id).filter(child => !placed.has(child.id) && placed.add(child.id));
        placedChildren.set(id, unplaced);
        nextLevel.push(...unplaced);
      }
      level = nextLevel;
    }

    const branch = (node: TNode): GraphTree<TNode>[] => {
      const kept = (placedChildren.get(node.id) ?? []).flatMap(branch);
      return keep(node) || kept.length > 0 ? [{ node, children: kept }] : [];
    };
    return roots().flatMap(branch);
  };

  return {
    node: (id: string): TNode | undefined => byId.get(id),
    parents,
    children,
    roots,
    ancestors: (id: string): TNode[][] => levels(id, parents),
    descendants: (id: string): TNode[][] => levels(id, children),
    tree,
  };
};

type GraphIndex<TNode extends GraphNode> = ReturnType<typeof indexGraph<TNode>>;

export { indexGraph };
export type { Graph, GraphIndex, GraphLink, GraphNode, GraphTree };
